import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post, put, SESSION_KEY } from "./client";
import { invalidateTask, taskKey } from "./testing";
import type { AddGitlabAllowedRequest } from "./types/AddGitlabAllowedRequest";
import type { AddGitlabLinkRequest } from "./types/AddGitlabLinkRequest";
import type { AddMemberRequest } from "./types/AddMemberRequest";
import type { AddProjectGitlabRepoRequest } from "./types/AddProjectGitlabRepoRequest";
import type { ChangePasswordRequest } from "./types/ChangePasswordRequest";
import type { ConnectGitlabRequest } from "./types/ConnectGitlabRequest";
import type { CreateGitlabRuleRequest } from "./types/CreateGitlabRuleRequest";
import type { CreateProjectRequest } from "./types/CreateProjectRequest";
import type { CreateUserRequest } from "./types/CreateUserRequest";
import type { CurrentUser } from "./types/CurrentUser";
import type { GitlabAllowedEntry } from "./types/GitlabAllowedEntry";
import type { GitlabConnectionView } from "./types/GitlabConnectionView";
import type { GitlabHookReport } from "./types/GitlabHookReport";
import type { GitlabLinkView } from "./types/GitlabLinkView";
import type { GitlabMrWithoutKey } from "./types/GitlabMrWithoutKey";
import type { GitlabRepoOption } from "./types/GitlabRepoOption";
import type { GitlabRuleView } from "./types/GitlabRuleView";
import type { LoginRequest } from "./types/LoginRequest";
import type { LoginResponse } from "./types/LoginResponse";
import type { Project } from "./types/Project";
import type { ProjectGitlabRepo } from "./types/ProjectGitlabRepo";
import type { ProjectGitlabRepos } from "./types/ProjectGitlabRepos";
import type { ProjectMember } from "./types/ProjectMember";
import type { RecoveryCodesResponse } from "./types/RecoveryCodesResponse";
import type { ReplaceGitlabTokenRequest } from "./types/ReplaceGitlabTokenRequest";
import type { SessionInfo } from "./types/SessionInfo";
import type { TaskGitlabDetail } from "./types/TaskGitlabDetail";
import type { TotpSetupResponse } from "./types/TotpSetupResponse";
import type { UpdateGitlabRuleRequest } from "./types/UpdateGitlabRuleRequest";
import type { UpdateMemberRequest } from "./types/UpdateMemberRequest";
import type { UpdateMeRequest } from "./types/UpdateMeRequest";
import type { UpdateProjectRequest } from "./types/UpdateProjectRequest";
import type { UpdateUserRequest } from "./types/UpdateUserRequest";
import type { UploadRequest } from "./types/UploadRequest";
import type { UploadTarget } from "./types/UploadTarget";
import type { UserOption } from "./types/UserOption";
import type { UserSummary } from "./types/UserSummary";
import type { UserWithPassword } from "./types/UserWithPassword";

const enc = encodeURIComponent;

// ───────────── Session / auth ─────────────

export function useSession() {
  return useQuery({ queryKey: SESSION_KEY, queryFn: () => get<SessionInfo>("/auth/session"), staleTime: 60_000 });
}

/** The signed-in user. Only call inside routes guarded by `RequireActive`. */
export function useMe(): CurrentUser {
  const { data } = useSession();
  if (!data?.user) throw new Error("useMe() used outside an active session");
  return data.user;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => post<LoginResponse>("/auth/login", body),
    onSuccess: (res) => qc.setQueryData<SessionInfo>(SESSION_KEY, { stage: res.stage, user: null }),
  });
}

export function useTotpSetup() {
  return useMutation({ mutationFn: () => post<TotpSetupResponse>("/auth/totp/setup") });
}

/** The setup page keeps showing the recovery codes after this, even though the session is now active. */
export function useTotpConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => post<RecoveryCodesResponse>("/auth/totp/confirm", { code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}

export function useTotpVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => post<LoginResponse>("/auth/totp/verify", { code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<void>("/auth/logout"),
    onSettled: () => {
      qc.clear();
      qc.setQueryData<SessionInfo>(SESSION_KEY, { stage: null, user: null });
    },
  });
}

// ───────────── Me ─────────────

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeRequest) => patch<CurrentUser>("/me", body),
    onSuccess: (user) => qc.setQueryData<SessionInfo>(SESSION_KEY, (s) => (s ? { ...s, user } : s)),
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: (body: ChangePasswordRequest) => post<void>("/me/password", body) });
}

export function useLogoutAll() {
  return useMutation({ mutationFn: () => post<void>("/me/logout-all") });
}

// ───────────── Users ─────────────

export function useUsers() {
  return useQuery({ queryKey: ["users"], queryFn: () => get<UserSummary[]>("/users") });
}

export function useUserOptions(enabled = true) {
  return useQuery({ queryKey: ["users", "options"], queryFn: () => get<UserOption[]>("/users/options"), enabled });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserRequest) => post<UserWithPassword>("/users", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserRequest }) => patch<UserSummary>(`/users/${enc(id)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useResetUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<UserWithPassword>(`/users/${enc(id)}/reset`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

// ───────────── Projects ─────────────

export function useProjects(archived = false) {
  return useQuery({
    queryKey: ["projects", { archived }],
    queryFn: () => get<Project[]>(`/projects${archived ? "?archived=true" : ""}`),
  });
}

export function useProject(key: string | undefined) {
  return useQuery({
    queryKey: ["project", key],
    queryFn: () => get<Project>(`/projects/${enc(key!)}`),
    enabled: !!key,
  });
}

function useInvalidateProject() {
  const qc = useQueryClient();
  return (project?: Project) => {
    if (project) qc.setQueryData(["project", project.key], project);
    void qc.invalidateQueries({ queryKey: ["projects"] });
  };
}

export function useCreateProject() {
  const invalidate = useInvalidateProject();
  return useMutation({
    mutationFn: (body: CreateProjectRequest) => post<Project>("/projects", body),
    onSuccess: invalidate,
  });
}

export function useUpdateProject(key: string) {
  const invalidate = useInvalidateProject();
  return useMutation({
    // Omitted fields stay unchanged on the server.
    mutationFn: (body: Partial<UpdateProjectRequest>) => patch<Project>(`/projects/${enc(key)}`, body),
    onSuccess: invalidate,
  });
}

export function useLogoUploadTarget(key: string) {
  return useMutation({
    mutationFn: (body: UploadRequest) => post<UploadTarget>(`/projects/${enc(key)}/logo`, body),
  });
}

export function useMembers(key: string) {
  return useQuery({
    queryKey: ["project", key, "members"],
    queryFn: () => get<ProjectMember[]>(`/projects/${enc(key)}/members`),
  });
}

/** Everyone who can be assigned in the project: members plus platform managers. */
export function useAssignees(key: string) {
  return useQuery({
    queryKey: ["project", key, "members", "assignable"],
    queryFn: () => get<ProjectMember[]>(`/projects/${enc(key)}/members?assignable=true`),
    // Lists across projects have no project to ask.
    enabled: !!key,
  });
}

function useInvalidateMembers(key: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["project", key] });
    void qc.invalidateQueries({ queryKey: ["projects"] });
    void qc.invalidateQueries({ queryKey: ["users"] });
  };
}

export function useAddMember(key: string) {
  const invalidate = useInvalidateMembers(key);
  return useMutation({
    mutationFn: (body: AddMemberRequest) => post<ProjectMember>(`/projects/${enc(key)}/members`, body),
    onSuccess: invalidate,
  });
}

export function useUpdateMember(key: string) {
  const invalidate = useInvalidateMembers(key);
  return useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: UpdateMemberRequest }) =>
      patch<ProjectMember>(`/projects/${enc(key)}/members/${enc(userId)}`, body),
    onSuccess: invalidate,
  });
}

export function useRemoveMember(key: string) {
  const invalidate = useInvalidateMembers(key);
  return useMutation({
    mutationFn: (userId: string) => del(`/projects/${enc(key)}/members/${enc(userId)}`),
    onSuccess: invalidate,
  });
}

// ───────────── GitLab: the connection (platform managers) ─────────────

const GITLAB_CONNECTION = ["gitlab-connection"] as const;
const GITLAB_ALLOWED = ["gitlab-allowed"] as const;

export function useGitlabConnection() {
  return useQuery({ queryKey: GITLAB_CONNECTION, queryFn: () => get<GitlabConnectionView>("/integrations/gitlab/connection") });
}

/** Anything that changes the connection can change what cards and repo rows show. */
function useGitlabConnectionChanged() {
  const qc = useQueryClient();
  return (connection: GitlabConnectionView) => {
    qc.setQueryData(GITLAB_CONNECTION, connection);
    void qc.invalidateQueries({ queryKey: ["gitlab-repos"] });
    void qc.invalidateQueries({ queryKey: ["users"] });
  };
}

export function useConnectGitlab() {
  const changed = useGitlabConnectionChanged();
  return useMutation({
    mutationFn: (body: ConnectGitlabRequest) => post<GitlabHookReport>("/integrations/gitlab/connection", body),
    onSuccess: (report) => changed(report.connection),
  });
}

export function useTestGitlab() {
  const changed = useGitlabConnectionChanged();
  return useMutation({
    mutationFn: () => post<GitlabConnectionView>("/integrations/gitlab/connection/test"),
    onSuccess: changed,
  });
}

export function useReplaceGitlabToken() {
  const changed = useGitlabConnectionChanged();
  return useMutation({
    mutationFn: (body: ReplaceGitlabTokenRequest) => put<GitlabHookReport>("/integrations/gitlab/connection/token", body),
    onSuccess: (report) => changed(report.connection),
  });
}

export function useRotateGitlabSecrets() {
  const changed = useGitlabConnectionChanged();
  return useMutation({
    mutationFn: () => post<GitlabHookReport>("/integrations/gitlab/connection/rotate-secrets"),
    onSuccess: (report) => changed(report.connection),
  });
}

export function useDisconnectGitlab() {
  const changed = useGitlabConnectionChanged();
  return useMutation({
    mutationFn: () => del<GitlabHookReport>("/integrations/gitlab/connection"),
    onSuccess: (report) => changed(report.connection),
  });
}

export function useGitlabAllowed() {
  return useQuery({ queryKey: GITLAB_ALLOWED, queryFn: () => get<GitlabAllowedEntry[]>("/integrations/gitlab/allowed") });
}

export function useAddGitlabAllowed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddGitlabAllowedRequest) => post<GitlabAllowedEntry>("/integrations/gitlab/allowed", body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: GITLAB_ALLOWED }),
  });
}

export function useRemoveGitlabAllowed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/integrations/gitlab/allowed/${enc(id)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: GITLAB_ALLOWED }),
  });
}

// ───────────── GitLab: a project's repos and rules (project managers) ─────────────

const gitlabPath = (key: string) => `/projects/${enc(key)}/gitlab`;

export function useProjectGitlabRepos(key: string) {
  return useQuery({ queryKey: ["gitlab-repos", key], queryFn: () => get<ProjectGitlabRepos>(`${gitlabPath(key)}/repos`) });
}

export function useGitlabRepoSearch(key: string, q: string) {
  return useQuery({
    queryKey: ["gitlab-repo-search", key, q],
    queryFn: () => get<GitlabRepoOption[]>(`${gitlabPath(key)}/repos/search?q=${enc(q)}`),
  });
}

/** A project's repos decide `Project.gitlab_enabled`, the environments its rules can pick from and what its apps show. */
function useGitlabReposChanged(key: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["gitlab-repos", key] });
    void qc.invalidateQueries({ queryKey: ["gitlab-environments", key] });
    void qc.invalidateQueries({ queryKey: ["apps", key] });
    void qc.invalidateQueries({ queryKey: ["project", key] });
    void qc.invalidateQueries({ queryKey: ["projects"] });
  };
}

export function useAddProjectGitlabRepo(key: string) {
  const changed = useGitlabReposChanged(key);
  return useMutation({
    mutationFn: (body: AddProjectGitlabRepoRequest) => post<ProjectGitlabRepo>(`${gitlabPath(key)}/repos`, body),
    onSuccess: changed,
  });
}

export function useRemoveProjectGitlabRepo(key: string) {
  const changed = useGitlabReposChanged(key);
  return useMutation({
    mutationFn: (id: string) => del(`${gitlabPath(key)}/repos/${enc(id)}`),
    onSuccess: changed,
  });
}

export function useRegisterGitlabHookAgain(key: string) {
  const changed = useGitlabReposChanged(key);
  return useMutation({
    mutationFn: (id: string) => post<ProjectGitlabRepo>(`${gitlabPath(key)}/repos/${enc(id)}/hook`),
    onSuccess: changed,
  });
}

export function useGitlabEnvironments(key: string) {
  return useQuery({ queryKey: ["gitlab-environments", key], queryFn: () => get<string[]>(`${gitlabPath(key)}/environments`) });
}

export function useGitlabMrsWithoutKey(key: string) {
  return useQuery({
    queryKey: ["gitlab-mrs-without-key", key],
    queryFn: () => get<GitlabMrWithoutKey[]>(`${gitlabPath(key)}/merge-requests-without-key`),
  });
}

export function useGitlabRules(key: string) {
  return useQuery({ queryKey: ["gitlab-rules", key], queryFn: () => get<GitlabRuleView[]>(`${gitlabPath(key)}/rules`) });
}

export function useCreateGitlabRule(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGitlabRuleRequest) => post<GitlabRuleView>(`${gitlabPath(key)}/rules`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["gitlab-rules", key] }),
  });
}

export function useUpdateGitlabRule(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateGitlabRuleRequest }) =>
      patch<GitlabRuleView>(`${gitlabPath(key)}/rules/${enc(id)}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["gitlab-rules", key] }),
  });
}

export function useDeleteGitlabRule(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`${gitlabPath(key)}/rules/${enc(id)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["gitlab-rules", key] }),
  });
}

// ───────────── GitLab: a task's code ─────────────

const taskGitlabPath = (key: string, task: string) => `/projects/${enc(key)}/tasks/${enc(task)}/gitlab`;

/** The Code tab's data, loaded when the tab opens. Lives under the task's query key, so task changes refresh it. */
export function useTaskGitlab(key: string, task: string) {
  return useQuery({
    queryKey: [...taskKey(key, task), "gitlab"],
    queryFn: () => get<TaskGitlabDetail>(taskGitlabPath(key, task)),
  });
}

export function useAddGitlabLink(key: string, task: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddGitlabLinkRequest) => post<GitlabLinkView>(`${taskGitlabPath(key, task)}/links`, body),
    onSuccess: () => invalidateTask(qc, key, task),
  });
}

export function useConfirmGitlabLink(key: string, task: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => post<GitlabLinkView>(`${taskGitlabPath(key, task)}/links/${enc(linkId)}/confirm`),
    onSuccess: () => invalidateTask(qc, key, task),
  });
}

export function useRemoveGitlabLink(key: string, task: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => del(`${taskGitlabPath(key, task)}/links/${enc(linkId)}`),
    onSuccess: () => invalidateTask(qc, key, task),
  });
}
